<script lang="ts" setup>
import type { IHeaderParams } from 'ag-grid-community';
import { ref, onMounted, onUnmounted, useTemplateRef } from 'vue';

type ExtendedHeaderParams = IHeaderParams & {
    tooltip: string;
};

const { params } = defineProps<{
    params: ExtendedHeaderParams;
}>();

const rootEl = useTemplateRef<HTMLElement>('root');
const sortDirection = ref<'ascending' | 'descending' | null>(null);
const observer = ref<MutationObserver | null>(null);

onMounted(() => {
    const headerCell = rootEl.value?.closest('.ag-header-cell');
    if (headerCell == null) return;

    const read = (): void => {
        const val = headerCell.getAttribute('aria-sort');
        sortDirection.value = (val === 'ascending' || val === 'descending') ? val : null;
    };

    observer.value = new MutationObserver(read);
    observer.value.observe(headerCell, { attributes: true, attributeFilter: ['aria-sort'] });
    read();
});

onUnmounted(() => {
    observer.value?.disconnect();
});
</script>

<template>
    <div
        ref="root"
        class="d-flex align-items-center gap-1 overflow-hidden ag-header-with-info"
        @click="params.progressSort($event.shiftKey)"
    >
        <span class="ag-header-cell-text text-truncate">{{ params.displayName }}</span>
        <SIcon
            v-if="sortDirection === 'ascending'"
            icon="ArrowUp"
            :size="14"
            class="sort-arrow"
        />
        <SIcon
            v-else-if="sortDirection === 'descending'"
            icon="ArrowDown"
            :size="14"
            class="sort-arrow"
        />
        <SIcon
            icon="Info"
            :size="14"
            v-tooltip="{ content: params.tooltip, theme: 'dark-tooltip' }"
            class="info-icon"
            @click.stop
        />
    </div>
</template>

<style scoped>
.ag-header-with-info {
    cursor: pointer;
    width: 100%;
    height: 100%;
}
</style>
